import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  FileBarChart,
  Plus,
  Send,
  Calendar,
  Clock,
  Mail,
  Trash2,
  Edit,
  Loader2,
  Play,
  Pause,
  X,
} from "lucide-react";
import { toast } from "sonner";

const REPORT_TYPES = [
  { value: "weekly_summary", label: "Weekly Summary" },
  { value: "daily_metrics", label: "Daily Metrics" },
  { value: "monthly_overview", label: "Monthly Overview" },
];

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const SECTIONS = [
  { value: "revenue", label: "Revenue Summary" },
  { value: "win_rates", label: "Performance Metrics" },
  { value: "alerts", label: "Recent Alerts" },
  { value: "top_performers", label: "Top Performers" },
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function ScheduledReportsPage() {
  const { api, user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [sending, setSending] = useState(null);
  
  const [newReport, setNewReport] = useState({
    name: "",
    report_type: "weekly_summary",
    frequency: "weekly",
    recipients: [],
    include_sections: ["revenue", "win_rates", "alerts", "top_performers"],
    day_of_week: 0,
    day_of_month: 1,
    hour: 9,
  });
  const [newEmail, setNewEmail] = useState("");

  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get("/scheduled-reports");
      setReports(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied");
      } else {
        toast.error("Failed to load reports");
      }
    } finally {
      setLoading(false);
    }
  };

  const createReport = async () => {
    if (!newReport.name || newReport.recipients.length === 0) {
      toast.error("Name and at least one recipient are required");
      return;
    }
    
    try {
      const response = await api.post("/scheduled-reports", newReport);
      setReports([response.data, ...reports]);
      setShowCreate(false);
      resetForm();
      toast.success("Report schedule created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create report");
    }
  };

  const updateReport = async () => {
    if (!editReport) return;
    
    try {
      const response = await api.put(`/scheduled-reports/${editReport.id}`, {
        name: editReport.name,
        recipients: editReport.recipients,
        include_sections: editReport.include_sections,
        enabled: editReport.enabled,
        day_of_week: editReport.day_of_week,
        day_of_month: editReport.day_of_month,
        hour: editReport.hour,
      });
      setReports(reports.map(r => r.id === editReport.id ? response.data : r));
      setEditReport(null);
      toast.success("Report updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update report");
    }
  };

  const deleteReport = async () => {
    try {
      await api.delete(`/scheduled-reports/${deleteId}`);
      setReports(reports.filter(r => r.id !== deleteId));
      toast.success("Report deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete report");
    } finally {
      setDeleteId(null);
    }
  };

  const sendNow = async (reportId) => {
    setSending(reportId);
    try {
      await api.post(`/scheduled-reports/${reportId}/send-now`);
      toast.success("Report sent successfully");
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSending(null);
    }
  };

  const toggleEnabled = async (report) => {
    try {
      await api.put(`/scheduled-reports/${report.id}`, { enabled: !report.enabled });
      setReports(reports.map(r => 
        r.id === report.id ? { ...r, enabled: !r.enabled } : r
      ));
      toast.success(`Report ${!report.enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error("Failed to update report");
    }
  };

  const addRecipient = (isEdit = false) => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    
    if (isEdit && editReport) {
      if (editReport.recipients.includes(newEmail)) {
        toast.error("Email already added");
        return;
      }
      setEditReport({ ...editReport, recipients: [...editReport.recipients, newEmail] });
    } else {
      if (newReport.recipients.includes(newEmail)) {
        toast.error("Email already added");
        return;
      }
      setNewReport({ ...newReport, recipients: [...newReport.recipients, newEmail] });
    }
    setNewEmail("");
  };

  const removeRecipient = (email, isEdit = false) => {
    if (isEdit && editReport) {
      setEditReport({ ...editReport, recipients: editReport.recipients.filter(e => e !== email) });
    } else {
      setNewReport({ ...newReport, recipients: newReport.recipients.filter(e => e !== email) });
    }
  };

  const toggleSection = (section, isEdit = false) => {
    if (isEdit && editReport) {
      const sections = editReport.include_sections.includes(section)
        ? editReport.include_sections.filter(s => s !== section)
        : [...editReport.include_sections, section];
      setEditReport({ ...editReport, include_sections: sections });
    } else {
      const sections = newReport.include_sections.includes(section)
        ? newReport.include_sections.filter(s => s !== section)
        : [...newReport.include_sections, section];
      setNewReport({ ...newReport, include_sections: sections });
    }
  };

  const resetForm = () => {
    setNewReport({
      name: "",
      report_type: "weekly_summary",
      frequency: "weekly",
      recipients: [],
      include_sections: ["revenue", "win_rates", "alerts", "top_performers"],
      day_of_week: 0,
      day_of_month: 1,
      hour: 9,
    });
    setNewEmail("");
  };

  const getFrequencyLabel = (report) => {
    if (report.frequency === "daily") {
      return `Daily at ${report.hour}:00`;
    } else if (report.frequency === "weekly") {
      return `${DAYS[report.day_of_week]}s at ${report.hour}:00`;
    } else {
      return `Day ${report.day_of_month} at ${report.hour}:00`;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileBarChart className="h-8 w-8 text-accent" />
            Scheduled Reports
          </h1>
          <p className="text-muted-foreground">
            Configure automated performance reports delivered to your inbox
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-report-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Report Schedule
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <FileBarChart className="h-4 w-4 text-accent" />
            <span className="text-sm">Total Reports</span>
          </div>
          <p className="text-2xl font-bold font-mono">{reports.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Play className="h-4 w-4 text-emerald-500" />
            <span className="text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold font-mono text-emerald-500">
            {reports.filter(r => r.enabled).length}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Calendar className="h-4 w-4 text-blue-500" />
            <span className="text-sm">Weekly</span>
          </div>
          <p className="text-2xl font-bold font-mono text-blue-500">
            {reports.filter(r => r.frequency === "weekly").length}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Clock className="h-4 w-4 text-purple-500" />
            <span className="text-sm">Daily</span>
          </div>
          <p className="text-2xl font-bold font-mono text-purple-500">
            {reports.filter(r => r.frequency === "daily").length}
          </p>
        </Card>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Report Schedules</CardTitle>
          <CardDescription>Manage your automated report delivery</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileBarChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No scheduled reports</p>
              <p className="text-sm">Create your first automated report to receive performance insights</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Sent</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id} data-testid={`report-row-${report.id}`}>
                    <TableCell className="font-medium">{report.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {REPORT_TYPES.find(t => t.value === report.report_type)?.label || report.report_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getFrequencyLabel(report)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{report.recipients?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        className={report.enabled 
                          ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" 
                          : "bg-slate-500/10 text-slate-500 border-slate-500/20"
                        }
                      >
                        {report.enabled ? "Active" : "Paused"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {report.last_sent 
                        ? new Date(report.last_sent).toLocaleString()
                        : "Never"
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendNow(report.id)}
                          disabled={sending === report.id || !report.recipients?.length}
                          data-testid={`send-now-${report.id}`}
                        >
                          {sending === report.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                        {canManage && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleEnabled(report)}
                            >
                              {report.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditReport(report)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteId(report.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Report Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Scheduled Report</DialogTitle>
            <DialogDescription>
              Set up a new automated report delivery
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Report Name *</Label>
              <Input
                placeholder="e.g., Weekly Performance Summary"
                value={newReport.name}
                onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                data-testid="report-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select
                  value={newReport.report_type}
                  onValueChange={(value) => setNewReport({ ...newReport, report_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={newReport.frequency}
                  onValueChange={(value) => setNewReport({ ...newReport, frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {newReport.frequency === "weekly" && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select
                  value={String(newReport.day_of_week)}
                  onValueChange={(value) => setNewReport({ ...newReport, day_of_week: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day, index) => (
                      <SelectItem key={index} value={String(index)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {newReport.frequency === "monthly" && (
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <Select
                  value={String(newReport.day_of_month)}
                  onValueChange={(value) => setNewReport({ ...newReport, day_of_month: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Send Time (Hour)</Label>
              <Select
                value={String(newReport.hour)}
                onValueChange={(value) => setNewReport({ ...newReport, hour: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <SelectItem key={hour} value={String(hour)}>
                      {hour.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Email Recipients *</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addRecipient(false)}
                />
                <Button type="button" onClick={() => addRecipient(false)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {newReport.recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newReport.recipients.map((email) => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1 pl-3">
                      <Mail className="h-3 w-3" />
                      {email}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 ml-1"
                        onClick={() => removeRecipient(email, false)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Report Sections</Label>
              <div className="grid grid-cols-2 gap-2">
                {SECTIONS.map(({ value, label }) => (
                  <div
                    key={value}
                    className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                      newReport.include_sections.includes(value) ? "border-accent bg-accent/5" : ""
                    }`}
                    onClick={() => toggleSection(value, false)}
                  >
                    <Checkbox
                      checked={newReport.include_sections.includes(value)}
                      onCheckedChange={() => toggleSection(value, false)}
                    />
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={createReport} data-testid="create-report-submit">
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Report Dialog */}
      <Dialog open={!!editReport} onOpenChange={() => setEditReport(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Report Schedule</DialogTitle>
          </DialogHeader>
          {editReport && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Report Name</Label>
                <Input
                  value={editReport.name}
                  onChange={(e) => setEditReport({ ...editReport, name: e.target.value })}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <Label>Enabled</Label>
                <Switch
                  checked={editReport.enabled}
                  onCheckedChange={(checked) => setEditReport({ ...editReport, enabled: checked })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Add recipient"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addRecipient(true)}
                  />
                  <Button type="button" onClick={() => addRecipient(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {editReport.recipients?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editReport.recipients.map((email) => (
                      <Badge key={email} variant="secondary" className="flex items-center gap-1 pl-3">
                        <Mail className="h-3 w-3" />
                        {email}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 ml-1"
                          onClick={() => removeRecipient(email, true)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Report Sections</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SECTIONS.map(({ value, label }) => (
                    <div
                      key={value}
                      className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                        editReport.include_sections?.includes(value) ? "border-accent bg-accent/5" : ""
                      }`}
                      onClick={() => toggleSection(value, true)}
                    >
                      <Checkbox
                        checked={editReport.include_sections?.includes(value)}
                        onCheckedChange={() => toggleSection(value, true)}
                      />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditReport(null)}>
              Cancel
            </Button>
            <Button onClick={updateReport}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scheduled report? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteReport} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
